'use client';

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDirectorSessionDetail } from '@/hooks/use-director-data';
import { SessionInfoCard } from '@/components/director/session-detail/session-info-card';
import { PlayerRoster } from '@/components/director/session-detail/player-roster';
import { AddPlayerSheet } from '@/components/director/session-detail/add-player-sheet';
import { formatTime } from '@/lib/format';

export default function DirectorSessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useDirectorSessionDetail(sessionId);
  const [addSheetOpen, setAddSheetOpen] = useState(false);

  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/director/sessions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sessions
        </Button>
        <div className="text-center py-12 text-muted-foreground">Session not found.</div>
      </div>
    );
  }

  const { session, isPast, players, sessionNotes, stats } = data;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/director/sessions')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Sessions
      </Button>

      {/* Header Card */}
      <SessionInfoCard
        session={session}
        isPast={isPast}
        stats={stats}
        formatDate={formatFullDate}
        formatTime={formatTime}
        onDeleted={() => navigate('/director/sessions')}
      />

      {/* Player List Card */}
      <PlayerRoster
        players={players}
        isPast={isPast}
        sessionId={session.id}
        onAddPlayers={() => setAddSheetOpen(true)}
      />

      {/* Coach Notes Card (past only) */}
      {isPast && sessionNotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Coach Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sessionNotes.map((note) => (
                <div
                  key={note.id}
                  className="p-3 bg-muted/50 rounded-lg text-sm text-foreground"
                >
                  {note.note}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Players Sheet (upcoming only) */}
      {!isPast && sessionId && (
        <AddPlayerSheet
          sessionId={sessionId}
          seriesId={session.seriesId ?? undefined}
          open={addSheetOpen}
          onOpenChange={setAddSheetOpen}
        />
      )}
    </div>
  );
}
